import { useState, useCallback, useRef } from 'react';
import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type { BaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
import type { UseUsers } from '../../types';
import fetchUsers from '../fetch-users';
import type { User } from '../fetch-users/types';

const handleError = (
	e: Error,
	setError: (arg1: ((arg1: undefined | Error) => undefined | Error) | undefined | Error) => void,
) => {
	setError(e);
	log.safeErrorWithoutCustomerData('platform.ui.user-picker', 'Failed to get users', e);
	throw e;
};

const useUsers: UseUsers = (baseUrl: BaseUrl, maxUsers?: number) => {
	const [data, setData] = useState<User[]>([]);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const currentQuery = useRef('');

	const action = ({ query = '', accountIds }: { query?: string; accountIds?: string[] }) => {
		setLoading(true);
		currentQuery.current = query;

		setError(undefined);

		if (accountIds && accountIds.length) {
			// Handle the initial fetch with IDs
			return Promise.all([
				fetchUsers({
					baseUrl,
					filter: { accountIds },
				}),
				fetchUsers({ baseUrl, maxUsers }),
			])
				.then((response) => {
					const flattened = flatten(response);
					const result = uniqBy(flattened, 'id');
					setData(result);
				})
				.catch((e) => handleError(e, setError))
				.finally(() => setLoading(false));
		}

		return fetchUsers({ baseUrl, filter: { searchString: query }, maxUsers })
			.then((response) => {
				if (query === currentQuery.current) {
					setData(response);
				}
			})
			.catch((e) => handleError(e, setError))
			.finally(() => setLoading(false));
	};

	// go/jfe-eslint
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const actionCb = useCallback(action, [baseUrl]);

	return [actionCb, { data, error, loading }] as const;
};

export default useUsers;
